import { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';

import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

type Props = {
  modifyProperty: (propertyMap: Partial<KaraokeConfig>) => void;
  strokeWidth: number | string;
  hasStickyBottom?: boolean;
};
const StrokeSelect = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { strokeWidth: _strokeWidth } = props;
  useEffect(() => {
    const parsed = parseStrokeWidth(_strokeWidth);
    setStrokeWidth(parsed.toString());
  }, [_strokeWidth]);
  const [strokeWidth, setStrokeWidth] = useState(
    _strokeWidth?.toString() || '0',
  );
  const [validationMsg, setValidationMsg] = useState<string | null>(null);
  return (
    <StrokeLines ref={ref} hasError={Boolean(validationMsg)}>
      <StrokeLineIcon>
        <StrokeLine
          height={1}
          color={Number(strokeWidth || 0) > 0 ? 'var(--accent)' : undefined}
        />
        <StrokeLine
          height={2}
          color={Number(strokeWidth || 0) > 0 ? 'var(--accent)' : undefined}
        />
        <StrokeLine
          height={3}
          color={Number(strokeWidth || 0) > 0 ? 'var(--accent)' : undefined}
        />
      </StrokeLineIcon>
      <StrokeInput
        type="number"
        value={strokeWidth}
        step={0.1}
        min={0}
        max={2}
        onChange={e => {
          setValidationMsg(null);
          const value = e.target.value;
          const num = Number(value);
          if (value && !isNaN(num)) {
            if (num >= 0 && num <= 2) {
              props.modifyProperty({
                stroke_width: `${value} vmin`,
                letter_spacing: getLetterSpacing(value),
              });
              setStrokeWidth(value);
            } else {
              setValidationMsg('Enter value 0-2');
            }
          } else if (value === '') {
            setStrokeWidth('');
          } else {
            setValidationMsg('Enter value 0-2');
          }
        }}
      />
      {validationMsg && <StrokeInputError>{validationMsg}</StrokeInputError>}
    </StrokeLines>
  );
});

const getLetterSpacing = (strokeWidth: string | number) => {
  const num = Number(strokeWidth);
  if (isNaN(num)) return '0%';
  if (num >= 0.1) return num * 50 + '%';
  return '0%';
};

export default StrokeSelect;

const parseStrokeWidth = (strokeWidth: number | string) => {
  if (typeof strokeWidth === 'number') return strokeWidth;
  const strokeWidthNum = Number(strokeWidth);
  if (isNaN(strokeWidthNum)) {
    const str = strokeWidth.replace(/\s+/g, '');
    let numStr = str.split('vmin')[0];
    const num = Number(numStr);
    if (isNaN(num)) numStr = str.split('px')[0];
    return num;
  }
  return strokeWidthNum;
};

const StrokeLines = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid
    ${({ hasError }) => (hasError ? 'var(--destructive)' : 'var(--border)')};
  border-radius: 8px;
  height: 42px;
  box-sizing: border-box;
  padding: 8px;
  position: relative;

  /*
   * Bordered-field-primitive hover/focus standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * Error state keeps the destructive border through hover + focus.
   * Focus-within catches keyboard focus on the inner stroke-width input.
   */
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: ${({ hasError }) =>
      hasError ? 'var(--destructive)' : 'var(--text-muted)'};
  }
  &:focus-within {
    border-color: ${({ hasError }) =>
      hasError ? 'var(--destructive)' : 'var(--accent)'};
  }
`;

const StrokeLineIcon = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StrokeLine = styled.span<{ height?: number; color?: string }>`
  width: 100%;
  height: ${props => props.height}px;
  background-color: ${props => props.color || 'var(--border)'};
`;

const StrokeInput = styled.input`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: normal;
  width: 32px;
  background: none;
  border: none;
  outline: none;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    display: none;
  }
`;

const StrokeInputError = styled.div`
  position: absolute;
  color: var(--destructive);
  font-size: 9px;
  bottom: -18px;
  left: 0;
  right: 0;
`;
